import date from 'date-and-time';
import { useMemo } from 'react';
import { useQueryParam } from '../../../utils/hooks/useQueryParam';
import {
  datePickerFormat,
  datePickerParamName,
  rangeDaysParamName,
  defaultRangeDays,
} from '../components/SMLDatePicker/consts';
import { useShareTokenContext } from '../contexts/ShareTokenContext';
import { formatUtcDay, latestCompleteUtcDay } from '../utils/utcDataDay';

export const useSMLDatePickerDateParam = () => {
  const { payload } = useShareTokenContext();
  const selectedDateParam = useQueryParam(datePickerParamName, null, '');
  const rangeDaysParam = useQueryParam(rangeDaysParamName, null, '');

  const selectedDate = useMemo(() => {
    // When in public/share-token mode, pin the selected date to the JWT's `d` field
    if (payload?.d) {
      const parsedPinned = date.parse(payload.d, datePickerFormat);
      if (!Number.isNaN(parsedPinned.getTime())) {
        return new Date(Date.UTC(parsedPinned.getFullYear(), parsedPinned.getMonth(), parsedPinned.getDate()));
      }
    }

    if (selectedDateParam) {
      // Create UTC date at midnight for the given date string
      // This ensures "2025-09-19" becomes "2025-09-19T00:00:00.000Z" not "2025-09-18T21:00:00.000Z"
      const parsedDate = date.parse(selectedDateParam, datePickerFormat);
      if (!Number.isNaN(parsedDate.getTime())) {
        // Create a new Date in UTC timezone at midnight
        return new Date(Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate()));
      }
    }

    // The data is daily in UTC. The local "yesterday" can still be today in UTC, which musicapi refuses.
    return latestCompleteUtcDay();
  }, [payload?.d, selectedDateParam]);

  // selectedDate is a UTC midnight, so format its UTC day. A local format shows the previous day west of UTC.
  const selectedDateValue = formatUtcDay(selectedDate);

  const rangeDays = useMemo(() => {
    // When in public/share-token mode, pin the range to the JWT's `r` field
    if (payload?.r !== undefined && payload.r > 0) {
      return payload.r;
    }

    if (rangeDaysParam) {
      const parsed = parseInt(rangeDaysParam, 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return defaultRangeDays;
  }, [payload?.r, rangeDaysParam]);

  return {
    selectedDate,
    selectedDateValue,
    rangeDays,
  };
};
